import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { Camera as CapCamera } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
import '../styles/camera.css';

export default function Camera() {

  const navigate = useNavigate();

  const [permissionStatus, setPermissionStatus] = useState('waiting');
  //const [isFlashOn, setIsFlashOn] = useState(false);
  //const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [isRecording, setIsRecording] = useState(false);

  const cameraStreamRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const RECORDING_LIMIT = 60 * 1000;
  const LONG_PRESS_THRESHOLD = 300;
  const longPressTimeoutRef = useRef(null);
  const isPointerDownOnCaptureButton = useRef(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const startYRef = useRef(null);
  const startZoomRef = useRef(null);
  const minZoom = 1;
  const maxZoom = 3;

  const log = (...args) => console.log('[Camera]', ...args);

  const initCameraStream = async () => {
    try {
      const constraints = {
        video: {
          facingMode: 'environment',
          focusMode: 'continuous',
        },
        audio: true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      cameraStreamRef.current = stream;
      setPermissionStatus('granted');
      log('getUserMedia success');
    } catch (err) {
      log('getUserMedia error:', err);
      setPermissionStatus('denied');
    }
  };

  const requestCameraAccess = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const checkResult = await CapCamera.checkPermissions();
        console.log('checkPermissions =>', checkResult);
  
        if (checkResult.camera !== 'granted') {
          const requestResult = await CapCamera.requestPermissions({
            permissions: ['camera', 'photos'],
          });
          console.log('requestPermissions =>', requestResult);
        }

        if (Capacitor.getPlatform() === 'android') {
          const storagePermission = await Filesystem.checkPermissions();
          console.log('checkPermissions (Filesystem) =>', storagePermission);

          if (storagePermission.publicStorage !== 'granted') {
            const storageRequest = await Filesystem.requestPermissions();
            console.log('requestPermissions (Filesystem) =>', storageRequest);
          }
        }
  
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        console.log('Microphone permission granted');
      }
      else {
        console.log('Running on the Web. No Capacitor plugin needed.');
      }
  
      await initCameraStream();
    } catch (error) {
      console.error('Permission request error:', error);
      setPermissionStatus('denied');
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      cameraStreamRef.current = null;
    }
    delete window._hiddenVideoElement;
  };

  useEffect(() => {
    setTimeout(() => {
      requestCameraAccess();
    }, 300);
  }, [permissionStatus]);

  const drawLoop = useCallback(() => {
    requestAnimationFrame(drawLoop);
  
    if (!cameraStreamRef.current) return;
    const videoTrack = cameraStreamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;
  
    const settings = videoTrack.getSettings();
    const vidWidth = settings.width || 1280;
    const vidHeight = settings.height || 720;
  
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  
    if (!window._hiddenVideoElement) {
      window._hiddenVideoElement = document.createElement('video');
      window._hiddenVideoElement.playsInline = true;
      window._hiddenVideoElement.muted = true;
      window._hiddenVideoElement.autoplay = true;
      window._hiddenVideoElement.srcObject = cameraStreamRef.current;
      window._hiddenVideoElement.play().catch((err) => {
        console.warn('Hidden video play error', err);
      });
    }
    const videoEl = window._hiddenVideoElement;
  
    if (videoEl.readyState < 2) return;
  
    const desiredRatio = 3 / 4;
    const feedRatio = vidWidth / vidHeight;
    let baseW, baseH, baseX, baseY;
  
    if (feedRatio > desiredRatio) {
      baseH = vidHeight;
      baseW = baseH * desiredRatio;
      baseX = (vidWidth - baseW) / 2;
      baseY = 0;
    }
    else {
      baseW = vidWidth;
      baseH = baseW / desiredRatio;
      baseX = 0;
      baseY = (vidHeight - baseH) / 2;
    }
  
    const zoomedW = baseW / zoomLevel;
    const zoomedH = baseH / zoomLevel;
    const zoomedX = baseX + (baseW - zoomedW) / 2;
    const zoomedY = baseY + (baseH - zoomedH) / 2;
  
    ctx.save();
    
    //if (isFrontCamera) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    //}

    ctx.drawImage(
      videoEl,
      zoomedX, zoomedY,
      zoomedW, zoomedH,
      0, 0,
      canvas.width, canvas.height
    );
    ctx.restore();
  }, [zoomLevel]);

  useEffect(() => {
    requestAnimationFrame(drawLoop);
  }, [drawLoop]);

  const startRecording = () => {
    if (!cameraStreamRef.current || !canvasRef.current) return;

    const canvasStream = canvasRef.current.captureStream(30);
    const audioTracks = cameraStreamRef.current.getAudioTracks();

    const combinedStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioTracks,
    ]);

    let options;

    if (MediaRecorder.isTypeSupported('video/mp4')) {
      options = { mimeType: 'video/mp4', videoBitsPerSecond: 5_000_000 };
    }
    else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
      options = { mimeType: 'video/webm; codecs=vp9' };
    }
    else if (MediaRecorder.isTypeSupported('video/webm')) {
      options = { mimeType: 'video/webm' };
    }
    else {
      console.error("No suitable MIME type found for this device");
      return;
    }
  
    console.log("Using MIME type:", options.mimeType);

    const mediaRecorder = new MediaRecorder(combinedStream, options);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const videoBlob = new Blob(recordedChunksRef.current, { type: 'video/mp4' });
      recordedChunksRef.current = [];

      const videoUrl = URL.createObjectURL(videoBlob);
      if (videoUrl) {
        navigate('/see-content', { state: { video: videoUrl } });
      }
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    setIsRecording(true);
    log('Recording started');

    setTimeout(() => {
      if (mediaRecorderRef.current?.state === "recording") {
        stopRecording();
      }
    }, RECORDING_LIMIT);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      log('Recording stopped');
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
  
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
  
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
  
    // if (isFrontCamera) {
    //   tempCtx.translate(tempCanvas.width, 0);
    //   tempCtx.scale(-1, 1);
    // }
  
    tempCtx.drawImage(canvas, 0, 0);
  
    const dataUrl = tempCanvas.toDataURL('image/png');
    log('Captured photo:', dataUrl.slice(0, 100) + '...');
    navigate('/see-content', { state: { photo: dataUrl } });
  };

  const handleDown = (e) => {
    //e.preventDefault();
    isPointerDownOnCaptureButton.current = true;
    setLongPressTriggered(false);

    const y = e.clientY ?? e.touches?.[0]?.clientY;
    startYRef.current = y;
    startZoomRef.current = zoomLevel;

    longPressTimeoutRef.current = setTimeout(() => {
      startRecording();
      setLongPressTriggered(true);
    }, LONG_PRESS_THRESHOLD);
  };

  const handleMove = (e) => {
    if (!isRecording || !longPressTriggered) return;
    if (startYRef.current == null || startZoomRef.current == null) return;

    const currentY = e.clientY ?? e.touches?.[0]?.clientY;
    const deltaY = startYRef.current - currentY;

    const zoomSensitivity = 0.01;
    let newZoom = startZoomRef.current + deltaY * zoomSensitivity;

    newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
    setZoomLevel(newZoom);
  };

  const handleUp = () => {
    //e.preventDefault();
    clearTimeout(longPressTimeoutRef.current);

    if (longPressTriggered) {
      stopRecording();
    }
    else if (isPointerDownOnCaptureButton.current) {
      capturePhoto();
    }

    isPointerDownOnCaptureButton.current = false;
    startYRef.current = null;
    startZoomRef.current = null;
  };

  const handleClose = () => {
    stopCamera();
    navigate(-1);
  };

  return (
    <div
      className="camera-container"
      //onPointerMove={handleMove}
      //onPointerUp={handleUp}
      onTouchMove={handleMove}
      onTouchEnd={handleUp}
    >
      <button className="close-button" onClick={handleClose}>
        <img src="assets/icons/close.png" alt="Close" />
      </button>

      {permissionStatus === 'waiting' && (
        <div className="loading-container">
          <CircularProgress size={40} sx={{ color: 'white' }} />
        </div>
      )}
      {permissionStatus === 'denied' && (
        <div className="permission-message">
          <p>Camera access was denied.<br/>Please enable it in device settings.</p>
        </div>
      )}

      {permissionStatus === 'granted' && (
        <>
          <canvas
            ref={canvasRef}
            className={`camera-video`}
            width={720}
            height={960}
          />
          <div className="capture-controls">
            <button
              className={`capture-button ${isRecording ? 'recording' : ''}`}
              //onPointerDown={handleDown}
              onTouchStart={handleDown}
            >
              <img
                src="assets/icons/capture.png"
                alt={isRecording ? 'Recording' : 'Capture'}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
}